import React, { useCallback, useRef, useState } from 'react'
import {
	Box, TextField, Typography,
} from '@mui/material'
import { AppDialog, AppDialogHeader, AppDialogBody, AppDialogFooter, AppDialogCancel, AppBulkEditAction, AppNote } from '@components'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { dsColorNew } from '@palette'

/**
 * A confirmation the operator can actually read.
 *
 * `window.confirm` was doing this job and it is the wrong tool twice
 * over. It renders as the browser's own chrome — "testing.rentacar
 * .dataseekers.com says" above the question — which looks like a
 * malware warning rather than part of the product. And it is a single
 * line of plain text, so the consequence of the action ("about 340
 * searches will be created and billed") has to be crammed into the
 * same sentence as the question, where it reads as noise and gets
 * clicked through.
 *
 * Here the question is the title, the consequence is the body, and a
 * destructive action gets a red button and an amber banner. Nothing is
 * defaulted to "OK": the confirm button carries the verb, so the
 * operator reads what they are about to do on the button itself.
 *
 * `input: { label, placeholder, required }` adds one text field; the
 * promise then resolves with the text (or `false` on cancel) instead
 * of `true` — an override needs its reason (CU-869fdg4nu). `disabled`
 * holds the confirm button while the caller is still loading what it
 * has to show.
 *
 * Usage — no provider, no context, one hook per component:
 *
 *     const [confirm, confirmDialog] = useConfirm()
 *     …
 *     if (!await confirm({ title: 'Delete this group?',
 *                          body: 'Items inside go back to the pool.',
 *                          confirmLabel: 'Delete group',
 *                          destructive: true })) return
 *     …
 *     return (<>{confirmDialog}<Rest/></>)
 */
export default function useConfirm() {
	const [request, setRequest] = useState(null)
	const [textValue, setTextValue] = useState('')
	const resolver = useRef(null)

	const confirm = useCallback((options) => new Promise((resolve) => {
		resolver.current = resolve
		setTextValue('')
		setRequest(options || {})
	}), [])
	// The caller may refresh what the dialog says while it is open (a
	// preview that arrives after the click): same promise, new content.
	const update = useCallback((patch) => setRequest(prev => prev ? { ...prev, ...patch } : prev), [])

	const settle = useCallback((answer) => {
		setRequest(null)
		// Cleared before resolving: a caller that opens a second confirm
		// from its own `await` would otherwise race this reset.
		const resolve = resolver.current
		resolver.current = null
		resolve?.(answer)
	}, [])

	const dialog = request ? (
		<AppDialog open onClose={() => settle(false)} maxWidth="xs">
			<AppDialogHeader icon={request.destructive ? 'triangle-exclamation' : 'circle-question'}
			                 title={request.title || 'Are you sure?'} onClose={() => settle(false)}/>
			<AppDialogBody sx={dialogBodySx}>
				{request.warning && (
					<AppNote severity="warning" sx={{ mb: 1.5 }}>
						{request.warning}
					</AppNote>
				)}
				{request.body && (
					<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted,
					                  lineHeight: 1.55 }}>
						{request.body}
					</Typography>
				)}
				{request.detail && (
					<Box sx={{ mt: 1.5, p: 1.25, bgcolor: dsColorNew.tableRowAlt,
					           border: `1px solid ${dsColorNew.grayBorder}`,
					           borderRadius: '10px', fontSize: 13,
					           color: dsColorNew.textMuted }}>
						{request.detail}
					</Box>
				)}
				{request.input && (
					<TextField autoFocus fullWidth size="small" multiline minRows={2}
					           label={request.input.label || 'Reason'}
					           placeholder={request.input.placeholder}
					           value={textValue}
					           onChange={(e) => setTextValue(e.target.value)}
					           sx={{ mt: 1.5 }}/>
				)}
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={() => settle(false)}>
					{request.cancelLabel || 'Cancel'}
				</AppDialogCancel>
				{/* The verb goes on the button, never "OK": the operator
				    should be able to read what happens without re-reading
				    the title. */}
				<AppBulkEditAction
				        disabled={!!request.disabled
				                  || (!!request.input?.required && !textValue.trim())}
				        onClick={() => settle(request.input ? textValue.trim() : true)}
				        sx={request.destructive
				            ? { bgcolor: dsColorNew.dangerRed, '&:hover': { bgcolor: dsColorNew.dangerRed, filter: 'brightness(0.92)' } }
				            : {}}>
					{request.confirmLabel || 'Confirm'}
				</AppBulkEditAction>
			</AppDialogFooter>
		</AppDialog>
	) : null

	// A third element, not a breaking change: every caller so far reads
	// the pair.
	return [confirm, dialog, update]
}
